import { useFormik } from 'formik';
import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { RegistrationLayout } from './components/RegistrationLayout';

import { Button, Dropdown, Form, InputRadio, Loader } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePermutive } from '@/hooks/usePermutive';
import useRegistration from '@/hooks/useRegistration';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  getLocationParty,
  getPartyProvince,
  getSchoolsLocation,
  registerUserData,
  getInitDataRegister,
} from '@/services';
import { SelectValue, DataPostRegister, DataPostTdeRegister } from '@/types';
import {
  TEXTS,
  OPTIONS_PROVINCES,
  datejs,
  schoolTourmantSchema,
  validateFieldError,
  parseToSelectOption,
  letterGenderById,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

type PlayTDEType = {
  playTde: boolean;
};

// Este type fue creado para manejar el estado de playTde dentro de formik y no en un useState nuevo
// Y de esa forma hacer mejores validaciones por YUP
type InitialValuesType = DataPostTdeRegister & PlayTDEType;
type RegistrationParams = DataPostRegister & {
  isPrimary: boolean;
};

export const RegistrationSchool = () => {
  const { registerData, handleSetRegisterData } = useRegistration();

  const navigate = useNavigate();
  const toast = useToast();
  const { state } = useLocation();
  const [showPrimary, setShowPrimary] = useState(true);
  const [showSecondary, setShowSecondary] = useState(true);
  const { isDesktop } = useIsDesktop();
  const { userInitialData } = useAuth();

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.schoolRegistration,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  if (userInitialData) {
    navigate(ROUTES.welcome, {
      replace: true,
    });
  }

  if (userInitialData?.tiene_equipo) {
    navigate(ROUTES.myTeam11, {
      replace: true,
    });
  }

  const { data: dataInit, isLoading } = useQuery(
    'initialDataRegister',
    async () => await getInitDataRegister(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { mutateAsync: saveRegistration } = useMutation(
    (formData: RegistrationParams) => {
      const { isPrimary, ...data } = formData;
      const payloadData = data;
      const withSchool = isPrimary;
      return registerUserData(payloadData, withSchool, 2);
    }
  );

  useEffect(() => {
    if (state === null) {
      navigate(ROUTES.registration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const formik = useFormik<InitialValuesType>({
    enableReinitialize: true,
    validationSchema: schoolTourmantSchema,
    initialValues: {
      isPrimary: true,
      playTde: true,
      province: null,
      schools: null,
      party: null,
      location: null,
      listSchools: [],
      listProvince: [],
      listParty: [],
      listLocation: [],
    },
    onSubmit: async (
      { schools, location, isPrimary, playTde },
      { setSubmitting }
    ) => {
      const {
        nombre,
        apellido,
        tipodoc,
        numdoc,
        numdoc2,
        dianac,
        mesnac,
        anionac,
        sexo,
        hincha,
        hinchaOtro,
        nombreEquipo,
        nroDocumentoExtranjero,
        docArg,
        idPais,
        email,
        email2,
        terceros,
        sms,
      } = state;

      const payload = {
        nombre,
        apellido,
        tipodoc,
        numdoc,
        numdoc2,
        dianac,
        mesnac,
        anionac,
        sexo,
        hincha,
        hinchaOtro,
        nombreEquipo,
        nroDocumentoExtranjero,
        docArg,
        idPais,
        email,
        email2,
        terceros,
        sms,
        ...(playTde &&
          schools &&
          location && {
            escuelaId: schools.value,
            localidadEscuelaId: location.value,
            tipoescuela: isPrimary ? 1 : 2,
          }),
        isPrimary,
      };

      try {
        const response = await saveRegistration(payload);
        setSubmitting(false);

        if (response && response.estado) {
          // Esta funcion se encarga de guardar los datos de la escuela en el store
          // para poder usarlos cada vez que se necesite autocompletar el formulario
          handleSetRegisterData({
            playTde: values.playTde,
            isPrimary: values.isPrimary,
            province: values.province,
            schools: values.schools,
            party: values.party,
            location: values.location,
          });

          navigate(ROUTES.registrationNameTeam, {
            state: {
              ...state,
              ...payload,
            },
          });
        } else {
          toast.show(response?.mensaje);
        }
      } catch (_error) {
        toast.show(TEXTS.callCenterError);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    isValidating,
    setFieldValue,
    setValues,
    resetForm,
    handleSubmit,
  } = formik;

  const handleSelectProvince = async (e: SelectValue) => {
    setFieldValue('province', e);
    if (e.value <= -1) return;
    const response = await getPartyProvince(e.value);

    if (response) {
      const listParty = parseToSelectOption(response.geografia);

      setValues({
        ...values,
        province: e,
        schools: null,
        party: null,
        location: null,
        listParty,
        listSchools: [],
        listLocation: [],
      });
    }
  };

  const handleSelectParty = async (e: SelectValue) => {
    if (e.value <= -1) return;
    const response = await getLocationParty(e.value);

    if (response) {
      const listLocation = parseToSelectOption(response.geografia);
      setValues({
        ...values,
        party: e,
        schools: null,
        location: null,
        listLocation,
        listSchools: [],
      });
    }
  };

  const handleSelectLocation = async (e: SelectValue) => {
    if (e.value <= -1) return;
    const schools = await getSchoolsLocation(e.value, !values.isPrimary);

    if (schools) {
      if (schools.length === 0) {
        toast.show(TEXTS.notFoundSchools);
      }

      const listSchools = schools.map((e) => {
        return {
          value: Number(e.id),
          label: e.n,
        };
      });

      setValues({
        ...values,
        location: e,
        schools: null,
        listSchools,
      });
    }
  };

  const handleSelectSchools = (e: SelectValue) => {
    if (e.value <= -1) return;
    setFieldValue('schools', e);
  };

  const primaryAndSecondaryValidation = useCallback(async () => {
    if (dataInit) {
      // Esta validacion se encarga de validar si se va a mostrar o no el input de escuela secundaria
      const primarySchoolDate = dataInit.fechaCompEscPrim
        ? datejs(dataInit.fechaCompEscPrim, 'DD/MM/YYYY').toDate()
        : null;
      const secondarySchoolDate = dataInit.fechaCompEscSec
        ? datejs(dataInit.fechaCompEscSec, 'DD/MM/YYYY').toDate()
        : null;

      const userBirthdate = datejs(
        dataInit.fechaNacimiento,
        'DD/MM/YYYY'
      ).toDate();

      if (
        dataInit.tieneDocArgentino &&
        datejs(userBirthdate).isAfter(primarySchoolDate)
      ) {
        setShowSecondary(false);

        if (registerData && registerData.isPrimary === false) {
          setFieldValue('isPrimary', true);
        }
      } else if (
        dataInit.tieneDocArgentino &&
        datejs(userBirthdate).isAfter(secondarySchoolDate)
      ) {
        setShowPrimary(true);
        setShowSecondary(true);
      }
      // Fin de la validacion
    }

    // Rellenado de los Dropdown con los datos que vienen del backend
    if (registerData) {
      const selectedProvinceId = registerData.province?.value;

      if (selectedProvinceId) {
        setFieldValue('province', registerData.province);

        const responseProvince = await getPartyProvince(selectedProvinceId);
        if (responseProvince) {
          const listParty = parseToSelectOption(responseProvince.geografia);
          setFieldValue('listParty', listParty);

          const selectedPartyId = registerData.party?.value;

          if (selectedPartyId) {
            setFieldValue('party', registerData.party);

            const responseParty = await getLocationParty(selectedPartyId);
            if (responseParty) {
              const listLocation = parseToSelectOption(responseParty.geografia);
              setFieldValue('listLocation', listLocation);

              const selectedLocationId = registerData.location?.value;

              if (selectedLocationId) {
                setFieldValue('location', registerData.location);

                const responseRawListSchools = await getSchoolsLocation(
                  selectedLocationId,
                  false
                );

                if (responseRawListSchools) {
                  const listSchools = responseRawListSchools.map((school) => ({
                    value: school.id,
                    label: school.n,
                  }));

                  setFieldValue('listSchools', listSchools);

                  const selectedSchoolId = registerData.schools?.value;

                  if (selectedSchoolId) {
                    setFieldValue('schools', registerData.schools);
                  }
                }
              }
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit, registerData]);

  useEffect(() => {
    primaryAndSecondaryValidation();
  }, [primaryAndSecondaryValidation]);

  const handleInputPrimary = () => {
    resetData();
    setFieldValue('isPrimary', true);
  };

  const handleInputSecondary = () => {
    resetData();
    setFieldValue('isPrimary', false);
  };

  const resetData = () => {
    resetForm();
  };

  return (
    <RegistrationLayout
      className="space-y-[1.75rem] flex flex-col"
      context="school"
      titleNavbar="Torneo de Escuelas"
    >
      {<Loader isVisible={isSubmitting || isLoading} timeout={isSubmitting} />}
      <header className="space-y-[1.75rem]">
        {isDesktop && (
          <h5 className="text-xl text-gdt-blue-dark">Torneo de Escuelas</h5>
        )}

        <InputRadio.Wrapper
          className="flex flex-col lg:flex-row gap-4 lg:gap-8"
          label="¿Querés participar del Torneo de Escuelas?"
        >
          <InputRadio.Input
            isChecked={values.playTde}
            label="Si"
            name="accept"
            value="accept"
            onChange={() => setFieldValue('playTde', true)}
          />
          <InputRadio.Input
            isChecked={!values.playTde}
            label="No"
            name="accept"
            value="accept"
            onChange={() => {
              setFieldValue('playTde', false);
            }}
          />
        </InputRadio.Wrapper>
      </header>

      {values.playTde ? (
        <Form.Form
          className="space-y-[1.75rem]"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <InputRadio.Wrapper
            className="flex flex-col lg:flex-row gap-4 lg:gap-8"
            label="Seleccioná tu escuela"
          >
            {showPrimary && (
              <InputRadio.Input
                isChecked={!!values.isPrimary}
                label="Primaria"
                name="school"
                value={`${values.isPrimary}`}
                onChange={handleInputPrimary}
              />
            )}
            {showSecondary && (
              <InputRadio.Input
                isChecked={!values.isPrimary}
                label="Secundaria"
                name="school"
                value={`${!values.isPrimary}`}
                onChange={handleInputSecondary}
              />
            )}
          </InputRadio.Wrapper>
          <Form.HorizontalGroup>
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                error={validateFieldError(touched.province, errors.province)}
                label="Provincia"
                options={OPTIONS_PROVINCES}
                selected={values?.province}
                onChange={handleSelectProvince}
              />
            </Form.Field>
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                error={validateFieldError(touched.party, errors.party)}
                label="Partido"
                options={values.listParty}
                selected={values?.party}
                onChange={handleSelectParty}
              />
            </Form.Field>
          </Form.HorizontalGroup>
          <Form.HorizontalGroup>
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                error={validateFieldError(touched.location, errors.location)}
                label="Localidad/Barrio"
                options={values.listLocation}
                selected={values?.location}
                onChange={handleSelectLocation}
              />
            </Form.Field>
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                error={validateFieldError(touched.schools, errors.schools)}
                label="Escuela"
                options={values.listSchools}
                selected={values.schools}
                onChange={handleSelectSchools}
              />
            </Form.Field>
          </Form.HorizontalGroup>
          <p className="text-xs lg:text-sm text-gdt-gray mt-5 font-normal">
            Ante cualquier inconveniente para encontrar tu escuela, ponete en
            contacto con nuestro Centro de Ayuda al 0800-333-2343.
          </p>
          <div className="flex items-center justify-between lg:mt-[5rem!important] mt-[4rem!important]">
            <Button
              className="w-full lg:flex-1 lg:px-0 lg:max-w-xs m-auto"
              id="btnRegisterWithSchool"
              type="submit"
              variant="primary"
            >
              Continuar
            </Button>
          </div>
        </Form.Form>
      ) : (
        <div className="flex flex-col flex-auto justify-end max-h-[29.25rem] h-[29.25rem]">
          <Form.Form
            className="space-y-[1.75rem]"
            loading={isSubmitting}
            onSubmit={handleSubmit}
          >
            <div className="flex justify-center 2xl:mt-[18rem!important]">
              <Button
                className="w-full lg:max-w-xs"
                disabled={isValidating}
                id="btnRegister_WithoutSchool"
                type="submit"
                variant="primary"
              >
                Continuar
              </Button>
            </div>
          </Form.Form>
        </div>
      )}
    </RegistrationLayout>
  );
};
